import blogsArchiveData from '../data/blogsArchive.json';

export function getSlugFromArticle(article) {
  if (!article) return '';
  if (article.link && article.link.includes('/@teamindiafgc/')) {
    return article.link.split('/@teamindiafgc/')[1].split('?')[0];
  }
  return article.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function getAllArchivedBlogs() {
  return blogsArchiveData || [];
}

export function getBlogBySlug(slug) {
  if (!slug) return null;
  const blogs = getAllArchivedBlogs();
  
  // Try exact slug match in link
  let match = blogs.find(b => b.link && b.link.includes(slug));
  if (match) return match;

  // Try title slug match
  const cleanSlug = slug.toLowerCase();
  match = blogs.find(b => {
    const titleSlug = b.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return titleSlug === cleanSlug || cleanSlug.includes(titleSlug) || titleSlug.includes(cleanSlug);
  });

  return match || null;
}

export function calculateReadTime(textOrHtml) {
  if (!textOrHtml) return '2 min read';
  const cleanText = textOrHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const wordCount = cleanText.split(' ').length;
  const minutes = Math.max(1, Math.ceil(wordCount / 200));
  return `${minutes} min read`;
}

// Enhance & sanitize Medium/RSS HTML for Medium-like typography
export function formatMediumArticleHtml(htmlContent) {
  if (!htmlContent) return '';
  
  const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
  
  // Remove tracking pixels, stats, or empty figcaptions
  doc.querySelectorAll('img[src*="stat?event"], figcaption:empty').forEach(el => el.remove());

  // 1. Process Embeds & YouTube / Medium Media links
  doc.querySelectorAll('iframe').forEach(iframe => {
    let src = iframe.getAttribute('src') || '';
    
    // Extract nested YouTube URL from Embedly widget iframe URL if present
    if (src.includes('embedly.com') || src.includes('youtube.com') || src.includes('youtu.be')) {
      let ytMatch = src.match(/youtube\.com%2Fembed%2F([a-zA-Z0-9_-]+)/i) || 
                   src.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/i) || 
                   src.match(/v%3D([a-zA-Z0-9_-]+)/i) || 
                   src.match(/v=([a-zA-Z0-9_-]+)/i);
      if (ytMatch) {
        src = `https://www.youtube.com/embed/${ytMatch[1]}`;
      }
    }
    
    iframe.setAttribute('src', src);
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.className = 'w-full aspect-video rounded-2xl my-10 border border-pragmatic/20 shadow-lg bg-midnight';
  });

  // Handle standalone medium.com/media/ href links or YouTube links
  doc.querySelectorAll('a').forEach(a => {
    const href = a.getAttribute('href') || '';
    const text = a.textContent.trim();

    // Check if it's a YouTube link
    let ytId = href.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/i) || href.match(/youtu\.be\/([a-zA-Z0-9_-]+)/i) || text.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/i);
    if (ytId) {
      const wrapper = doc.createElement('div');
      wrapper.className = 'my-10 w-full';
      wrapper.innerHTML = `<iframe src="https://www.youtube.com/embed/${ytId[1]}" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen class="w-full aspect-video rounded-2xl border border-pragmatic/20 shadow-lg bg-midnight"></iframe>`;
      if (a.parentNode && a.parentNode.tagName === 'P' && a.parentNode.childNodes.length === 1) {
        a.parentNode.replaceChild(wrapper, a);
      } else {
        a.replaceWith(wrapper);
      }
      return;
    }

    // Check if it's a raw medium.com/media/ link
    if (href.includes('medium.com/media/') || text.includes('medium.com/media/')) {
      if (a.parentNode && a.parentNode.tagName === 'P' && a.parentNode.childNodes.length === 1) {
        a.className = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-dwarfpony/10 text-dwarfpony font-bold text-sm hover:bg-dwarfpony/20 transition-colors my-4 border border-dwarfpony/20';
        a.innerHTML = `<span>View Interactive Media Embed</span> <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>`;
        return;
      }
    }

    a.className = 'text-dwarfpony hover:text-dwarfpony-dark font-semibold underline underline-offset-4 decoration-dwarfpony/40 hover:decoration-dwarfpony transition-colors';
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });

  // 2. Format Figures & Captions
  doc.querySelectorAll('figure').forEach(fig => {
    fig.className = 'my-10 text-center w-full overflow-hidden';
  });

  doc.querySelectorAll('figcaption').forEach(fc => {
    fc.className = 'text-center text-xs sm:text-sm font-sans text-heavyrain italic mt-3 mb-2 px-4 block max-w-xl mx-auto';
  });

  // 3. Format Images (Uncropped, crisp, centered)
  doc.querySelectorAll('img').forEach(img => {
    img.className = 'w-full h-auto rounded-2xl shadow-md my-4 border border-pragmatic/20 object-contain mx-auto block max-w-full';
  });

  // 4. Format Blockquotes
  doc.querySelectorAll('blockquote').forEach(bq => {
    bq.className = 'border-l-4 border-dwarfpony pl-6 italic text-midnight text-xl sm:text-2xl bg-dwarfpony/5 p-6 rounded-r-2xl my-10 font-serif leading-relaxed shadow-xs';
  });

  // 5. Format Subtitles, Lead Paragraphs vs Section Headings
  doc.querySelectorAll('h1, h2, h3, h4').forEach(h => {
    const text = h.textContent.trim();
    
    // Check if it's a byline or subtitle
    if (text.toLowerCase().startsWith('by ') || text.toLowerCase().includes('by stem education trust') || text.toLowerCase().includes('official partner')) {
      h.className = 'font-serif text-heavyrain italic text-lg sm:text-xl font-normal my-4 leading-relaxed border-none block';
      h.querySelectorAll('strong, b').forEach(st => st.className = 'font-normal italic');
    } 
    // Check if it's a long intro/standfirst paragraph wrapped in h3/h4 by Medium
    else if (text.length > 100) {
      h.className = 'font-serif text-midnight font-medium text-xl sm:text-2xl leading-relaxed mb-8 border-none block tracking-normal';
      h.querySelectorAll('strong, b').forEach(st => st.className = 'font-medium text-midnight');
    } 
    // Actual section titles (short headings)
    else {
      h.className = 'font-sans font-extrabold text-midnight mt-12 mb-5 tracking-tight leading-snug text-2xl sm:text-3xl border-b border-pragmatic/15 pb-2 block';
      h.querySelectorAll('strong, b').forEach(st => st.className = 'font-extrabold text-midnight');
    }
  });

  // 6. Format Paragraphs & Lead Paragraph
  const paragraphs = doc.querySelectorAll('p');
  paragraphs.forEach((p, idx) => {
    const text = p.textContent.trim();

    if (text.toLowerCase().startsWith('by ') && idx < 3) {
      p.className = 'font-serif text-heavyrain italic text-lg sm:text-xl font-normal my-4 leading-relaxed block';
      p.querySelectorAll('strong, b').forEach(st => st.className = 'font-normal italic');
    } else if (idx === 0) {
      p.className = 'font-serif text-midnight text-xl sm:text-2xl leading-relaxed mb-8 font-medium';
    } else {
      p.className = 'font-serif text-deepwalnut text-lg sm:text-xl leading-relaxed mb-8 tracking-normal';
    }
  });

  // 7. Format Lists
  doc.querySelectorAll('ul').forEach(ul => {
    ul.className = 'list-disc list-outside space-y-3 mb-8 font-serif text-deepwalnut text-lg sm:text-xl pl-6 leading-relaxed';
  });
  doc.querySelectorAll('ol').forEach(ol => {
    ol.className = 'list-decimal list-outside space-y-3 mb-8 font-serif text-deepwalnut text-lg sm:text-xl pl-6 leading-relaxed';
  });

  return doc.body.innerHTML;
}
